import './ApprovalList.scss';
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { IDocumentTable, TableColumn } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { useLocation, useNavigate } from 'react-router';
import ApproveListTable from '@organisms/ApproveListTable';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { DocumentModalContext } from '@/context/Contexts';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { collectTableColumns, TableRow } from './tableColumns';
import { createTableFormBridge } from './tableFormBridge';
import { buildTableCellRender, ICellRenderDeps } from './tableCells';
import { useColumnRules } from './tableRuleHooks';
import { RowViewButton } from './tableButtons';
import TableForm from '@organisms/TableData/ModalForms/TableForm';
import SignCheckContainer from '@organisms/TableData/ModalForms/SignCheckContainer';

/** Заглушка на месте листа: история целиком уехала в таб «Ход согласования».
 *  Отдельным компонентом — чтобы сам лист не требовал контекста роутера. */
const ApprovalListMovedNotice = () => {
    const navigate = useNavigate();
    const location = useLocation();
    return (
        <div className="approval-list-moved">
            История согласования теперь на вкладке{' '}
            {/* не <a href="#...">: из-за <base href> голый hash-href сбрасывает путь документа */}
            <button
                type="button"
                className="approval-list-moved__link"
                onClick={() => {
                    void navigate({ pathname: location.pathname, hash: 'routeapproval' });
                    // Tabs синхронизирует активную вкладку с URL только на popstate
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }}
            >
                «Ход согласования»
            </button>{' '}
            — вместе с маршрутом, текущим этапом и будущими шагами.
        </div>
    );
};

export interface IApprovalListProps<TFieldValues extends object = object> {
    table: IDocumentTable;
    formMethods: UseFormReturn<TFieldValues>;
    fields: Record<string, IFieldElem>;
    docId?: string;
}

/**
 * Лист согласования (`approvalListMode`) — те же строки таблицы, но показаны
 * циклами и уровнями, а не списком. Ячейки рисует общий рендер таблицы форм,
 * поэтому стили-формулы колонок и виды значений здесь те же, что в таблице.
 */
const ApprovalList = <TFieldValues extends object = object>({
    table,
    formMethods,
    fields,
    docId,
}: IApprovalListProps<TFieldValues>) => {
    const dataField = fields[table.key];
    const watched = useWatch({ control: formMethods.control, name: dataField?.name as never, disabled: !dataField });
    const rows: TableRow[] = useMemo(() => (Array.isArray(watched) ? watched : []), [watched]);
    const [viewState, setViewState] = useState<{ rowKey: unknown; rowKeys: unknown[] } | null>(null);
    const viewRows = useMemo(() => {
        if (!viewState) return [];
        const byKey = new Map(rows.map((row) => [row['|NUM'], row]));
        return viewState.rowKeys.map((key) => byKey.get(key)).filter((row): row is TableRow => !!row);
    }, [rows, viewState]);

    // Финальная стадия перехода на единый «Ход согласования» (бэк-флаг
    // UnifiedRouteApprovalHideLegacy): блок на форме скрывается — история целиком
    // живёт в табе. В модалке документа unified-таб скрыт, поэтому там лист остаётся.
    const runtime = useContext(DocumentRuntimeContext);
    const modalCtx = useContext(DocumentModalContext);
    const documentScheme = useTypedSelector((state) => state.document.scheme);
    const hideApprovalList =
        runtime.mode === 'supportPreview' ? false : !modalCtx.data && !!documentScheme?.hideApprovalList;

    // Лист согласования только показывает: правку строк ведёт маршрут, а не форма,
    // поэтому мост нужен ровно для формул стилей и формы просмотра строки.
    const bridge = useMemo(
        () => createTableFormBridge({ fields, formMethods, isEdit: false, isNew: false, setError: () => {} }),
        [fields, formMethods],
    );
    const columnRules = useColumnRules(table, bridge, formMethods);
    const sources = useMemo(
        () => new Map(collectTableColumns(table).map((source) => [source.column.key, source])),
        [table],
    );

    const cellDeps = useMemo<ICellRenderDeps>(
        () => ({
            rulesFor: (row, columnId) => columnRules.for(row, columnId),
            onInstantCommit: () => {},
            skipRequired: true,
            skipPattern: true,
            skipLength: true,
            displayOnly: true,
        }),
        [columnRules],
    );

    const renderCell = useCallback(
        (row: TableRow, column: TableColumn) => {
            const source = sources.get(column.key);
            if (!source) return null;
            return buildTableCellRender(source.column, source.columnId, cellDeps)(row);
        },
        [sources, cellDeps],
    );

    const renderRowActions = useCallback(
        (row: TableRow, levelRows: TableRow[]) => (
            <RowViewButton
                onOpen={() =>
                    setViewState({
                        rowKey: row['|NUM'],
                        rowKeys: levelRows.map((levelRow) => levelRow['|NUM']),
                    })
                }
            />
        ),
        [],
    );

    if (hideApprovalList) return <ApprovalListMovedNotice />;

    return (
        <>
            <ApproveListTable
                value={rows}
                table={table}
                renderCell={renderCell}
                // досчёт и сброс правил строки перерисовывают только её: ячейки грида берутся из кэша строк
                rowVersion={columnRules.versionOf}
                renderRowActions={table.showRowForm !== false ? renderRowActions : undefined}
                docId={docId}
            />
            {viewState && viewRows.length > 0 && (
                <TableForm
                    table={table}
                    docId={docId}
                    title={table.name}
                    isEdit={false}
                    onClose={() => setViewState(null)}
                    // variables карточки и признаки загрузки таблиц-линков: формулы формы строки
                    // (видимость, calc) считаются и в режиме просмотра
                    parentFieldMap={fields}
                    getParentFields={bridge.getParentFields}
                    rowIndex={Math.max(
                        0,
                        viewRows.findIndex((row) => row['|NUM'] === viewState.rowKey),
                    )}
                    rowArray={viewRows}
                >
                    {(row) =>
                        table.signCheck ? <SignCheckContainer signCheck={table.signCheck} rowData={row} /> : null
                    }
                </TableForm>
            )}
        </>
    );
};

// Пара к useDeferredValue в Table — как у EditTable: срочный рендер со старым отложенным
// режимом переиспользует готовое поддерево, тяжёлый проход остаётся только фоновому.
export default memo(ApprovalList) as typeof ApprovalList;
